import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  BleCharacteristic,
  BleClient,
  BleDevice,
  BleService,
  numbersToDataView,
} from '@capacitor-community/bluetooth-le';
import { RecentPosOrder } from '@adminpages/domain-models';

const printerConfigStorageKey = 'adminpages.pos.printerConfig';
const defaultChunkSize = 20;
const defaultChunkDelayMs = 35;
const bleConnectionTimeoutMs = 20_000;

export type PosPrinterAdapter = 'ble_escpos' | 'browser';
export type PosPrinterWriteMode = 'withResponse' | 'withoutResponse';
export type PosPrinterPaperWidth = '58mm' | '80mm';

export interface PosPrinterConfig {
  adapter: PosPrinterAdapter;
  name: string;
  deviceId: string;
  serviceUuid: string;
  characteristicUuid: string;
  writeMode: PosPrinterWriteMode;
  paperWidth: PosPrinterPaperWidth;
  chunkSize: number;
  chunkDelayMs: number;
  autoPrintReceipt: boolean;
  cashDrawerEnabled: boolean;
}

export interface PosReceiptPrintContext {
  businessName: string;
  currency: string;
}

export interface PosPrinterCandidate {
  device: BleDevice;
  service: BleService;
  characteristic: BleCharacteristic;
}

@Injectable({ providedIn: 'root' })
export class PosPrintingService {
  readonly printerConfig = signal<PosPrinterConfig | null>(this.loadConfig());
  readonly busy = signal(false);
  readonly statusMessage = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);
  private bleInitialization: Promise<void> | null = null;

  canUseBle(): boolean {
    return Capacitor.isNativePlatform();
  }

  async configureBlePrinter(): Promise<PosPrinterConfig> {
    this.resetMessages();
    this.busy.set(true);

    try {
      await this.initializeBle();
      const device = await BleClient.requestDevice({ displayMode: 'list' });
      await this.connectDevice(device.deviceId);

      try {
        const services = await BleClient.getServices(device.deviceId);
        const candidate = this.findWritableCharacteristic(device, services);

        if (!candidate) {
          throw new Error('La impresora no expuso una caracteristica BLE escribible.');
        }

        const config: PosPrinterConfig = {
          adapter: 'ble_escpos',
          name: candidate.device.name || 'Impresora BLE',
          deviceId: candidate.device.deviceId,
          serviceUuid: candidate.service.uuid,
          characteristicUuid: candidate.characteristic.uuid,
          writeMode: candidate.characteristic.properties.writeWithoutResponse
            ? 'withoutResponse'
            : 'withResponse',
          paperWidth: '58mm',
          chunkSize: defaultChunkSize,
          chunkDelayMs: defaultChunkDelayMs,
          autoPrintReceipt: false,
          cashDrawerEnabled: false,
        };

        this.saveConfig(config);
        this.statusMessage.set(`Impresora guardada: ${config.name}`);

        return config;
      } finally {
        await this.safeDisconnect(device.deviceId);
      }
    } catch (error) {
      const message = this.messageFrom(error, 'No se pudo configurar la impresora BLE.');
      this.errorMessage.set(message);
      throw new Error(message);
    } finally {
      this.busy.set(false);
    }
  }

  saveConfig(config: PosPrinterConfig): void {
    const normalized = this.normalizeConfig(config);
    this.printerConfig.set(normalized);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(printerConfigStorageKey, JSON.stringify(normalized));
    }
  }

  clearConfig(): void {
    this.printerConfig.set(null);

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(printerConfigStorageKey);
    }

    this.statusMessage.set('Configuracion de impresora eliminada.');
    this.errorMessage.set(null);
  }

  async printTest(): Promise<void> {
    const bytes = new EscPosReceiptBuilder('58mm')
      .init()
      .center()
      .bold(true)
      .textLine('ADMINPAGES POS')
      .bold(false)
      .textLine('Prueba de impresion')
      .left()
      .separator()
      .textLine(new Date().toLocaleString('es-MX'))
      .feed(3)
      .cut()
      .build();

    await this.printBytes(bytes);
  }

  async openCashDrawer(): Promise<void> {
    await this.printBytes([0x1b, 0x70, 0x00, 0x32, 0xfa]);
  }

  async printReceipt(order: RecentPosOrder, context: PosReceiptPrintContext): Promise<boolean> {
    const config = this.printerConfig();

    if (!config || config.adapter === 'browser') {
      return false;
    }

    const bytes = this.receiptBytes(order, context, config);
    await this.printBytes(bytes, config);

    return true;
  }

  private async printBytes(bytes: number[], config = this.printerConfig()): Promise<void> {
    this.resetMessages();

    if (!config || config.adapter !== 'ble_escpos') {
      throw new Error('No hay una impresora BLE configurada.');
    }

    this.busy.set(true);

    try {
      await this.initializeBle();
      await this.restoreDevice(config.deviceId);
      await this.connectDevice(config.deviceId);

      try {
        const chunkSize = Math.max(1, config.chunkSize || defaultChunkSize);

        for (let offset = 0; offset < bytes.length; offset += chunkSize) {
          const chunk = bytes.slice(offset, offset + chunkSize);
          const value = numbersToDataView(chunk);

          if (config.writeMode === 'withoutResponse') {
            await BleClient.writeWithoutResponse(
              config.deviceId,
              config.serviceUuid,
              config.characteristicUuid,
              value,
            );
          } else {
            await BleClient.write(
              config.deviceId,
              config.serviceUuid,
              config.characteristicUuid,
              value,
            );
          }

          await this.delay(config.chunkDelayMs || defaultChunkDelayMs);
        }
      } finally {
        await this.safeDisconnect(config.deviceId);
      }

      this.statusMessage.set('Ticket enviado a la impresora.');
    } catch (error) {
      const message = this.messageFrom(error, 'No se pudo imprimir en la impresora BLE.');
      this.errorMessage.set(message);
      throw new Error(message);
    } finally {
      this.busy.set(false);
    }
  }

  private receiptBytes(
    order: RecentPosOrder,
    context: PosReceiptPrintContext,
    config: PosPrinterConfig,
  ): number[] {
    const builder = new EscPosReceiptBuilder(config.paperWidth);

    builder
      .init()
      .center()
      .bold(true)
      .textLine(context.businessName)
      .bold(false)
      .textLine(`${this.sourceLabel(order.source)} - ${this.statusLabel(order.status)}`)
      .textLine(order.createdAt)
      .separator()
      .left()
      .bold(true)
      .textLine(order.folio)
      .bold(false)
      .textLine(`Cliente: ${order.customerName}`);

    if (order.customerPhone) {
      builder.textLine(`Tel: ${order.customerPhone}`);
    }

    builder.separator();

    for (const item of order.items) {
      builder.textLine(item.name);
      builder.columns(`${item.quantity} x ${this.money(item.unitPrice, context.currency)}`, this.money(item.total, context.currency));

      for (const addon of item.addons) {
        builder.textLine(`+ ${addon.name}`);
      }
    }

    builder
      .separator()
      .columns('Total', this.money(order.total, context.currency))
      .columns('Pagado', this.money(order.paidTotal, context.currency))
      .columns('Saldo', this.money(order.balanceDue, context.currency));

    if (order.payments.length > 0) {
      builder.separator();

      for (const payment of order.payments) {
        builder.columns(
          `${this.methodLabel(payment.method)} ${this.paymentStatusLabel(payment.status)}`,
          this.money(payment.amount, context.currency),
        );
      }
    }

    builder
      .separator()
      .center()
      .textLine('Gracias por tu compra.')
      .feed(3);

    if (config.cashDrawerEnabled) {
      builder.openDrawer();
    }

    return builder.cut().build();
  }

  private async initializeBle(): Promise<void> {
    if (!this.canUseBle()) {
      throw new Error('La impresion BLE requiere la app instalada en iOS o Android.');
    }

    if (!this.bleInitialization) {
      this.bleInitialization = BleClient.initialize({ androidNeverForLocation: true }).catch((error) => {
        this.bleInitialization = null;
        throw error;
      });
    }

    await this.bleInitialization;

    if (!(await BleClient.isEnabled())) {
      await BleClient.requestEnable();
    }
  }

  private async restoreDevice(deviceId: string): Promise<void> {
    const devices = await BleClient.getDevices([deviceId]);

    if (!devices.some((device) => device.deviceId === deviceId)) {
      throw new Error('La impresora BLE guardada ya no esta disponible. Vuelve a configurarla.');
    }
  }

  private async connectDevice(deviceId: string): Promise<void> {
    await BleClient.connect(deviceId, undefined, {
      timeout: bleConnectionTimeoutMs,
      skipDescriptorDiscovery: true,
    });
  }

  private findWritableCharacteristic(device: BleDevice, services: BleService[]): PosPrinterCandidate | null {
    for (const service of services) {
      for (const characteristic of service.characteristics) {
        if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
          return { device, service, characteristic };
        }
      }
    }

    return null;
  }

  private loadConfig(): PosPrinterConfig | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(printerConfigStorageKey);

      return raw ? this.normalizeConfig(JSON.parse(raw) as Partial<PosPrinterConfig>) : null;
    } catch {
      return null;
    }
  }

  private normalizeConfig(config: Partial<PosPrinterConfig>): PosPrinterConfig {
    return {
      adapter: config.adapter === 'ble_escpos' ? 'ble_escpos' : 'browser',
      name: config.name || 'Impresora BLE',
      deviceId: config.deviceId || '',
      serviceUuid: config.serviceUuid || '',
      characteristicUuid: config.characteristicUuid || '',
      writeMode: config.writeMode === 'withResponse' ? 'withResponse' : 'withoutResponse',
      paperWidth: config.paperWidth === '80mm' ? '80mm' : '58mm',
      chunkSize: Number.isFinite(Number(config.chunkSize)) ? Number(config.chunkSize) : defaultChunkSize,
      chunkDelayMs: Number.isFinite(Number(config.chunkDelayMs)) ? Number(config.chunkDelayMs) : defaultChunkDelayMs,
      autoPrintReceipt: Boolean(config.autoPrintReceipt),
      cashDrawerEnabled: Boolean(config.cashDrawerEnabled),
    };
  }

  private async safeDisconnect(deviceId: string): Promise<void> {
    try {
      await BleClient.disconnect(deviceId);
    } catch {
      // Some printer firmwares disconnect immediately after a print job.
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));
  }

  private resetMessages(): void {
    this.statusMessage.set(null);
    this.errorMessage.set(null);
  }

  private money(value: number, currency: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private sourceLabel(source: RecentPosOrder['source']): string {
    return {
      pos: 'POS',
      ecommerce: 'Ecommerce',
      panel: 'Panel',
    }[source];
  }

  private statusLabel(status: RecentPosOrder['status']): string {
    return {
      confirmed: 'Confirmado',
      quoted: 'Cotizado',
      cancelled: 'Cancelado',
    }[status];
  }

  private methodLabel(method: RecentPosOrder['payments'][number]['method']): string {
    return {
      cash: 'Efectivo',
      transfer: 'Transfer',
      card: 'Tarjeta',
      other: 'Otro',
    }[method];
  }

  private paymentStatusLabel(status: string): string {
    return {
      approved: 'OK',
      completed: 'OK',
      pending: 'Pendiente',
      failed: 'Fallido',
      cancelled: 'Cancelado',
    }[status] ?? status;
  }

  private messageFrom(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }
}

class EscPosReceiptBuilder {
  private readonly bytes: number[] = [];
  private readonly lineWidth: number;

  constructor(paperWidth: PosPrinterPaperWidth) {
    this.lineWidth = paperWidth === '80mm' ? 48 : 32;
  }

  init(): this {
    return this.command(0x1b, 0x40);
  }

  left(): this {
    return this.command(0x1b, 0x61, 0x00);
  }

  center(): this {
    return this.command(0x1b, 0x61, 0x01);
  }

  bold(enabled: boolean): this {
    return this.command(0x1b, 0x45, enabled ? 0x01 : 0x00);
  }

  textLine(value: string): this {
    for (const line of this.wrap(value)) {
      this.text(line);
      this.newLine();
    }

    return this;
  }

  columns(left: string, right: string): this {
    const cleanLeft = this.clean(left);
    const cleanRight = this.clean(right);
    const availableLeft = Math.max(1, this.lineWidth - cleanRight.length - 1);

    for (const line of this.wrap(cleanLeft, availableLeft)) {
      if (line.length === cleanLeft.length) {
        this.text(line.padEnd(this.lineWidth - cleanRight.length, ' ') + cleanRight);
      } else {
        this.text(line);
      }

      this.newLine();
    }

    return this;
  }

  separator(): this {
    return this.textLine('-'.repeat(this.lineWidth));
  }

  feed(lines: number): this {
    for (let index = 0; index < lines; index += 1) {
      this.newLine();
    }

    return this;
  }

  openDrawer(): this {
    return this.command(0x1b, 0x70, 0x00, 0x32, 0xfa);
  }

  cut(): this {
    return this.command(0x1d, 0x56, 0x00);
  }

  build(): number[] {
    return [...this.bytes];
  }

  private command(...bytes: number[]): this {
    this.bytes.push(...bytes);

    return this;
  }

  private text(value: string): this {
    this.bytes.push(...this.encode(this.clean(value)));

    return this;
  }

  private newLine(): this {
    this.bytes.push(0x0a);

    return this;
  }

  private wrap(value: string, width = this.lineWidth): string[] {
    const cleanValue = this.clean(value);
    const lines: string[] = [];

    for (let offset = 0; offset < cleanValue.length || offset === 0; offset += width) {
      lines.push(cleanValue.slice(offset, offset + width));
    }

    return lines;
  }

  private clean(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
  }

  private encode(value: string): number[] {
    return Array.from(value).map((character) => this.encodeCharacter(character));
  }

  private encodeCharacter(character: string): number {
    const code = character.charCodeAt(0);
    const cp850: Record<string, number> = {
      á: 0xa0,
      é: 0x82,
      í: 0xa1,
      ó: 0xa2,
      ú: 0xa3,
      Á: 0xb5,
      É: 0x90,
      Í: 0xd6,
      Ó: 0xe0,
      Ú: 0xe9,
      ñ: 0xa4,
      Ñ: 0xa5,
      ü: 0x81,
      Ü: 0x9a,
      '¿': 0xa8,
      '¡': 0xad,
    };

    if (cp850[character] !== undefined) {
      return cp850[character];
    }

    return code >= 0x20 && code <= 0x7e ? code : 0x3f;
  }
}
